import React, { useState, useEffect, useCallback, useRef } from 'react';
import ChatBot, { ChatBotProvider, useChatHistory } from 'react-chatbotify';
import MarkdownRenderer from '@rcb-plugins/markdown-renderer';
import io from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import KeycloakService from '../services/KeycloakService';

// Remove the CSS injection since we're using wrapper div approach

const CourseSuggestionChatbot = () => {
  const { authenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const chatRef = useRef(null);
  const CHATBOT_API_URL = process.env.REACT_APP_CHATBOT_API_URL || 'http://localhost:3666';
  const [connectionError, setConnectionError] = useState(null);

  // Initialize socket connection
  useEffect(() => {
    console.log('Component mounted, initial isMaximized:', isMaximized);
  }, [isMaximized]);

  useEffect(() => {
    console.log('isMaximized state changed to:', isMaximized);
  }, [isMaximized]);

  useEffect(() => {
    if (authenticated) {
      const token = KeycloakService.getToken();
      
      if (!token) {
        console.error('No token available for socket connection');
        setConnectionError('Authentication token not available. Please try logging in again.');
        return;
      }
      
      console.log('Initializing socket with token:', token ? 'Token available' : 'No token');
      
      const socketInstance = io(CHATBOT_API_URL, {
        auth: { token },
        transports: ['websocket', 'polling']
      });

      socketInstance.on('connect', () => {
        console.log('Chatbot socket connected');
        setIsConnected(true);
        setConnectionError(null);
        
        // Join conversation to get conversationId
        socketInstance.emit('join_conversation', {});
      });

      socketInstance.on('disconnect', () => {
        console.log('Chatbot socket disconnected');
        setIsConnected(false);
      });

      socketInstance.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setIsConnected(false);
        setConnectionError('Failed to connect to chatbot service. Please check if the service is running.');
      });

      socketInstance.on('conversation_joined', (data) => {
        console.log('Joined conversation:', data);
        if (data.conversationId) {
          setConversationId(data.conversationId);
        }
      });

      // Handle user message confirmation
      socketInstance.on('message_received', (data) => {
        console.log('User message confirmed:', data);
        if (data.conversationId && !conversationId) {
          setConversationId(data.conversationId);
        }
      });

      // Handle AI typing indicators
      socketInstance.on('ai_typing_start', (data) => {
        console.log('AI started typing:', data);
      });

      socketInstance.on('ai_typing_stop', (data) => {
        console.log('AI stopped typing:', data);
      });

      socketInstance.on('error', (error) => {
        console.error('Socket error:', error);
        setIsConnected(false);
      });

      // Handle message errors
      socketInstance.on('message-error', (data) => {
        console.error('Message error:', data);
      });

      setSocket(socketInstance);

      return () => {
        if (socketInstance) {
          // Remove all event listeners
          socketInstance.off('connect');
          socketInstance.off('disconnect');
          socketInstance.off('connect_error');
          socketInstance.off('conversation_joined');
          socketInstance.off('message_received');
          socketInstance.off('ai_typing_start');
          socketInstance.off('ai_typing_stop');
          socketInstance.off('error');
          socketInstance.off('message-error');
          socketInstance.disconnect();
        }
      };
    }
  }, [authenticated, CHATBOT_API_URL, conversationId]);

  // Handle streaming messages
  const handleStreamMessage = useCallback(async (params) => {
    const { userInput, streamMessage, endStreamMessage } = params;

    if (!socket || !isConnected) {
      endStreamMessage();
      if (connectionError) {
        return `**Connection Error** ❌\n\n${connectionError}\n\nPlease try refreshing the page or contact support if the issue persists.`;
      }
      return '**Connecting...** ⏳\n\nPlease wait while connecting to the chatbot service...';
    }

    return new Promise((resolve) => {
      let fullResponse = '';
      let streamingTimeout;
      let hasReceivedResponse = false;
      
      // Handle real-time streaming from WebSocket
      const handleStreamChunk = (data) => {
        console.log('✅ Received stream chunk:', data);
        hasReceivedResponse = true;
        
        // Extract chunk text based on socket service structure
        let chunkText = '';
        if (typeof data === 'string') {
          chunkText = data;
        } else if (data && typeof data === 'object') {
          // Handle socket service chunk format: { conversationId, chunk: { content: "text" }, type }
          if (data.chunk && typeof data.chunk === 'object') {
            chunkText = data.chunk.content || data.chunk.text || data.chunk.chunk || '';
          } else {
            chunkText = data.chunk || data.text || data.content || '';
          }
        }
        
        console.log('📝 Extracted chunk text:', chunkText);
        
        if (chunkText && typeof chunkText === 'string') {
          fullResponse += chunkText;
          streamMessage(fullResponse);
          console.log('📤 Updated response length:', fullResponse.length);
        } else {
          console.warn('⚠️ No valid chunk text found in:', data);
        }
      };
      
      const handleStreamComplete = (data) => {
        console.log('✅ Stream completed:', data);
        hasReceivedResponse = true;
        // Update conversationId if provided
        if (data && data.conversationId && !conversationId) {
          setConversationId(data.conversationId);
        }
        cleanup();
        endStreamMessage();
        resolve();
      };
      
      const handleStreamError = (error) => {
        console.error('❌ Stream error:', error);
        hasReceivedResponse = true;
        cleanup();
        // Provide fallback response on error
        const errorResponse = `## ⚠️ Service Temporarily Unavailable\n\nI'm having trouble connecting to the AI service right now. This could be due to:\n\n- 🔧 **Backend service maintenance**\n- 🌐 **Network connectivity issues**\n- ⚡ **High server load**\n\n### What you can try:\n- **Refresh the page** and try again\n- **Check your internet connection**\n- **Try again in a few moments**\n\n> I apologize for the inconvenience. Please try your question again shortly!`;
        
        let text = "";
        let index = 0;
        const errorInterval = setInterval(() => {
          if (index < errorResponse.length) {
            const chunk = errorResponse.slice(index, index + 5);
            text += chunk;
            streamMessage(text);
            index += 5;
          } else {
            clearInterval(errorInterval);
            endStreamMessage();
            resolve();
          }
        }, 20);
      };
      
      const cleanup = () => {
        if (socket) {
          socket.off('message-chunk', handleStreamChunk);
          socket.off('message-complete', handleStreamComplete);
          socket.off('message-error', handleStreamError);
        }
        if (streamingTimeout) {
          clearTimeout(streamingTimeout);
        }
      };
      
      // Set up WebSocket event listeners
      if (socket) {
        console.log('🚀 Setting up WebSocket listeners and sending message:', userInput);
        
        socket.on('message-chunk', handleStreamChunk);
        socket.on('message-complete', handleStreamComplete);
        socket.on('message-error', handleStreamError);
        
        // Send message to backend using socket service format
        socket.emit('send_message', {
          content: userInput,
          messageType: 'text'
        });
        
        console.log('📤 Message sent via WebSocket');
        
        // Timeout fallback with response
        streamingTimeout = setTimeout(() => {
          console.log('⏰ Stream timeout - providing fallback response');
          
          if (!hasReceivedResponse) {
            // Provide a helpful fallback response
            const fallbackResponse = `## 🤖 AI Assistant\n\nI received your message: "**${userInput}**"\n\n### Current Status:\n- ✅ **Message received successfully**\n- ⏳ **AI service response delayed**\n- 🔄 **Attempting to process your request**\n\n### Possible Solutions:\n\n**For Course Recommendations:**\n- Browse our **[Course Catalog](/courses)** directly\n- Check out **Popular Categories** in the navigation\n- Use the **Search Bar** to find specific topics\n\n**For Technical Support:**\n- Try **refreshing the page**\n- **Clear your browser cache**\n- **Contact our support team** if issues persist\n\n> Please try sending your message again, or explore our course catalog while we work on resolving this issue!`;
            
            let text = "";
            let index = 0;
            const fallbackInterval = setInterval(() => {
              if (index < fallbackResponse.length) {
                const chunk = fallbackResponse.slice(index, index + 8);
                text += chunk;
                streamMessage(text);
                index += 8;
              } else {
                clearInterval(fallbackInterval);
                endStreamMessage();
                resolve();
              }
            }, 25);
          }
          
          cleanup();
        }, 10000); // Reduced timeout to 10 seconds for better UX
      } else {
        console.log('❌ No socket available');
        endStreamMessage();
        resolve();
      }
    });
  }, [socket, isConnected, conversationId, connectionError]);

  // Handle maximize/minimize toggle

  // Handle login redirect for unauthorized users
  const handleLoginRedirect = useCallback(() => {
    // Redirect to login page
    window.location.href = '/login';
  }, []);

  // Handle non-authenticated users with login prompt
  const handleGuestMessage = useCallback((params) => {
    const { streamMessage, endStreamMessage } = params;
    
    return new Promise((resolve) => {
      // Simulate typing delay for better UX
      setTimeout(() => {
        const loginPrompt = "## 🔐 Login Required\n\n**To access our AI Course Assistant, please log in to your account.**\n\n### Benefits of logging in:\n- 🤖 **Personalized AI recommendations**\n- 📚 **Custom learning paths**\n- 💾 **Save conversation history**\n- 🎯 **Tailored course suggestions**\n\n> **[Click here to login →](/login)**\n\n*As a guest, you can still browse our course catalog without AI assistance.*";
        
        let text = "";
        let index = 0;
        
        // Simulate streaming for login prompt
        const streamInterval = setInterval(() => {
          if (index < loginPrompt.length) {
            const chunk = loginPrompt.slice(index, index + 3);
            text += chunk;
            streamMessage(text);
            index += 3;
          } else {
            clearInterval(streamInterval);
            endStreamMessage();
            resolve();
            // Auto-redirect after showing message
            setTimeout(() => {
              handleLoginRedirect();
            }, 3000);
          }
        }, 50);
      }, 1000);
    });
  }, [handleLoginRedirect]);

  const HistoryLoader = ({ socket, conversationId }) => {
    const { setMessages } = useChatHistory();

    useEffect(() => {
      if (socket && conversationId) {
        socket.emit('load_history', { conversationId });

        const handleHistory = (data) => {
          console.log('Received chat history:', data);
          setMessages(data.messages.map((msg) => ({
            type: msg.sender === 'ai' ? 'bot' : 'user',
            content: msg.content
          })));
        };

        socket.on('chat_history', handleHistory);

        return () => socket.off('chat_history', handleHistory);
      }
    }, [socket, conversationId, setMessages]);

    return null;
  };

  // Plugin configuration
  const plugins = [MarkdownRenderer({ autoConfig: true })];

  // Chatbot configuration
const flow = {
    start: {
      message: (params) => {
  if (params.messages && params.messages.length > 0) {
    return "";
  }
  return authenticated 
    ? "# Hello! 👋\n\n**Welcome** to your *Course Assistant*! \n\nI can help you with:\n- 📚 Finding perfect courses\n- 🎯 Personalized recommendations\n- 🛤️ Creating learning paths\n- ❓ Answering questions about courses\n\n> What would you like to learn today?"
    : "# 🔐 Login Required\n\n**Hi there!** To access our AI Course Assistant, you'll need to log in first.\n\n### Why login?\n- 🤖 **Get personalized recommendations**\n- 📊 **Track your learning progress**\n- 💬 **Save chat history**\n- 🎯 **Receive tailored suggestions**\n\n> **Please log in to continue** or browse our courses as a guest.";
},
      path: authenticated ? "user_input" : "login_required",
      renderMarkdown: ["BOT", "USER"]
    },
    user_input: {
      message: handleStreamMessage,
      path: "user_input",
      renderMarkdown: ["BOT", "USER"]
    },
    login_required: {
      message: handleGuestMessage,
      path: "login_required",
      renderMarkdown: ["BOT", "USER"]
    }
  };

  const baseSettings = {
    general: {
      primaryColor: "#1890ff",
      secondaryColor: "#f0f0f0",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    chatButton: {
      icon: authenticated ? "🤖" : "🔐"
    },
    chatInput: {
      placeholder: authenticated 
        ? "Ask me about courses, skills, or learning paths..."
        : "Please log in to access AI assistance...",
      disabled: !authenticated,
      showCharacterCount: authenticated
    },
    chatHistory: {
      storageKey: null,
      maxEntries: 30
    },
    botBubble: {
      showAvatar: true,
      avatar: "🤖"
    },
    userBubble: {
      showAvatar: true,
      avatar: "👤"
    },
    footer: {
      text: "Powered by JetSkill AI"
    },
    tooltip: {
      mode: "CLOSE",
      text: authenticated 
        ? "Need course suggestions? Chat with our AI!"
        : "Login required to access AI Course Assistant"
    }
  };

  const normalSettings = {
    ...baseSettings,
    chatButton: { disabled: true },
    general: { embedded: true },
    header: {
      title: authenticated ? 'Course AI Assistant' : 'Login Required',
      showAvatar: true,
      avatar: authenticated ? '🎓' : '🔐',
      buttons: [
        <button
          key="maximize"
          onClick={() => { setIsChatOpen(false); setIsMaximized(true); }}
          style={{
            background: 'none',
            border: 'none',
            color: '#ffffff',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Maximize Chat"
        >
          🗖
        </button>,
        <button
          key="close"
          onClick={() => setIsChatOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#ffffff',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title="Close Chat"
        >
          ✕
        </button>
      ]
    }
  };

  const maximizedSettings = {
    ...baseSettings,
    general: {
      ...baseSettings.general,
      embedded: true
    },
    header: {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🎓</span>
            <span>Course AI Assistant</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsMaximized(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              title="Minimize Chat"
            >
              🗗
            </button>
            <button
              onClick={() => setIsMaximized(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              title="Close Chat"
            >
              ✕
            </button>
          </div>
        </div>
      ),
      showAvatar: false,
      buttons: []
    }
  };

  const baseStyles = {
    chatButtonStyle: {
      backgroundColor: "#1890ff",
      width: "60px",
      height: "60px",
      fontSize: "24px"
    },
    headerStyle: {
      background: "linear-gradient(90deg, #1890ff, #40a9ff)",
      color: "#ffffff"
    },
    chatInputStyle: {
      border: "1px solid #d9d9d9",
      borderRadius: "6px"
    },
    botBubbleStyle: {
      backgroundColor: "#f6f6f6",
      color: "#333333"
    },
    userBubbleStyle: {
      backgroundColor: "#1890ff",
      color: "#ffffff"
    }
  };

  const normalStyles = {
    ...baseStyles,
    chatWindowStyle: {
      backgroundColor: "#ffffff",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    bodyStyle: {
      backgroundColor: "#fafafa"
    }
  };

  const maximizedStyles = {
    ...baseStyles,
    chatWindowStyle: {
      position: 'fixed',
      top: '5vh',
      left: '5vw',
      width: '90vw',
      height: '90vh',
      zIndex: 9999,
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      border: '1px solid #e0e0e0',
      overflow: 'hidden',
      backgroundColor: '#ffffff'
    },
    headerStyle: {
      ...baseStyles.headerStyle,
      borderRadius: '12px 12px 0 0'
    },
    bodyStyle: {
      backgroundColor: '#fafafa',
      height: 'calc(90vh - 120px)',
      overflowY: 'auto'
    }
  };

  return (
    <>
      {isMaximized && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setIsMaximized(false)}
        />
      )}
      {!isChatOpen && !isMaximized && (
        <button
          onClick={() => setIsChatOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#1890ff',
            color: 'white',
            fontSize: '24px',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          title="Open Chat"
        >
          💬
        </button>
      )}
      {isChatOpen && !isMaximized && (
        <div ref={chatRef} style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
          <ChatBot
            flow={flow}
            settings={normalSettings}
            styles={normalStyles}
            plugins={plugins}
          />
        </div>
      )}
      {isMaximized && (
        <ChatBot
          flow={flow}
          settings={maximizedSettings}
          styles={maximizedStyles}
          plugins={plugins}
        />
      )}
    </>
  );
};

export default CourseSuggestionChatbot;